import React, { type FC } from 'react';
import type { PrimitiveType } from 'intl-messageformat';
import { Flex, Text } from '@atlaskit/primitives';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import messages from '../../messages.tsx';
import type { ComponentState } from '../../types.tsx';

interface ToggleButtonContentProps {
	shouldDisplay: boolean;
	resultNumber?: number;
	error?: Error | unknown | undefined;
	formatMessage: (message: MessageDescriptorV2, values?: Record<string, PrimitiveType>) => string;
	messages: {
		headerTitle: MessageDescriptorV2;
		errorMessage: MessageDescriptorV2;
		noItemsMessage: MessageDescriptorV2;
		viewItemsMessage?: MessageDescriptorV2;
		hideSuggestionsMessage?: MessageDescriptorV2;
		findingResultsMessage?: MessageDescriptorV2;
		inactiveMessage?: MessageDescriptorV2;
	};
	currentStep: ComponentState;
}

/**
 * Content component for the toggle button that shows different states:
 * 1. No items found
 * 2. Error message
 * 3. View items with count
 * 4. Hide suggestions
 */
export const ToggleButtonContent: FC<ToggleButtonContentProps> = ({
	shouldDisplay,
	resultNumber,
	formatMessage,
	messages: customMessages,
	currentStep,
}) => {
	if (!shouldDisplay) {
		if (currentStep === 'INACTIVE') {
			return (
				<Text weight="medium" color="color.text.subtle">
					{formatMessage(customMessages?.inactiveMessage || messages.inactiveMessage)}
				</Text>
			);
		}
		if (!resultNumber || resultNumber === 0) {
			return (
				<Text weight="medium" color="color.text">
					{formatMessage(customMessages.noItemsMessage)}
				</Text>
			);
		}
		return (
			<Flex alignItems="center" gap="space.100">
				<Text weight="medium" color="color.text">
					{formatMessage(customMessages?.viewItemsMessage || messages.showNumResults, {
						numResults: resultNumber,
					})}
				</Text>
			</Flex>
		);
	}
	return (
		<Text weight="medium" color="color.text">
			{formatMessage(customMessages?.hideSuggestionsMessage || messages.hideSuggestions)}
		</Text>
	);
};
